import { useMemo, useState } from "react"

import { useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type { DynamicTitlesConfig } from "./edit-dynamic-titles-dialog"
import type { ResponsesCompactionConfig } from "./edit-responses-compaction-dialog"
import type { StreamGapMitigationConfig } from "./edit-stream-gap-mitigation-dialog"

import { fetchFeatureConfig, updateFeatureConfig, updateFeaturesStatusBatch } from "@/api/admin-api"
import { PlugZap, Shield, ShieldCheck } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { Switch } from "@/components/ui/switch"
import { normalizeReasoningPresets } from "@/features/chat/utils/reasoning-presets"
import { queryKeys } from "@/lib/query-keys"

import { AccessPolicyDialog } from "./access-policy-dialog"
import { CollapsibleManagementSection } from "./collapsible-management-section"
import { DelegationModeControl } from "./delegation-mode-control"
import { EditDynamicTitlesDialog } from "./edit-dynamic-titles-dialog"
import {
  EditExplorerFastRouterDialog,
  type ExplorerFastRouterConfig,
} from "./edit-explorer-fast-router-dialog"
import { EditExplorerSteerDialog, type ExplorerSteerConfig } from "./edit-explorer-steer-dialog"
import { EditFeatureDialog } from "./edit-feature-dialog"
import { EditResponsesCompactionDialog } from "./edit-responses-compaction-dialog"
import { EditStreamGapMitigationDialog } from "./edit-stream-gap-mitigation-dialog"
import { FeaturesTable } from "./features-table"
import { PersonalPolicyConfirmDialog } from "./personal-policy-dialogs"
import {
  useAdminFeatureAccessPolicyQuery,
  useAdminFeatureBaseAccessPolicyQuery,
  useAdminFeaturesQuery,
  useClearFeatureAccessPolicyMutation,
  useUpdateFeatureAccessPolicyMutation,
  useUpdateFeatureBaseAccessPolicyMutation,
  useUpdateFeatureStatusMutation,
} from "../hooks/admin-queries"
import { usePersonalPolicyConfirmation } from "../hooks/use-personal-policy-confirmation"
import { isPolicyConfigured } from "../utils/access-policy"

export function FeaturesManagementCard() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [updatingFeatureId, setUpdatingFeatureId] = useState<string | undefined>()
  const [isUpdatingAll, setIsUpdatingAll] = useState(false)
  const [isAccessDialogOpen, setIsAccessDialogOpen] = useState(false)
  const [accessFeatureId, setAccessFeatureId] = useState<string | undefined>()
  const [isBaseAccessDialogOpen, setIsBaseAccessDialogOpen] = useState(false)

  // Config dialog state
  const [isConfigDialogOpen, setIsConfigDialogOpen] = useState(false)
  const [editingFeatureId, setEditingFeatureId] = useState<string | null>(null)
  const [editingFeatureRawId, setEditingFeatureRawId] = useState<string | null>(null)
  const [dynamicTitlesConfig, setDynamicTitlesConfig] = useState<DynamicTitlesConfig | null>(null)
  const [responsesCompactionConfig, setResponsesCompactionConfig] =
    useState<ResponsesCompactionConfig | null>(null)
  const [streamGapMitigationConfig, setStreamGapMitigationConfig] =
    useState<StreamGapMitigationConfig | null>(null)
  const [explorerFastRouterConfig, setExplorerFastRouterConfig] =
    useState<ExplorerFastRouterConfig | null>(null)
  const [explorerSteerConfig, setExplorerSteerConfig] = useState<ExplorerSteerConfig | null>(null)

  const { data: features, isLoading } = useAdminFeaturesQuery()
  const baseAccessQuery = useAdminFeatureBaseAccessPolicyQuery(true)
  const baseAccessPolicy = baseAccessQuery.data
  const updateBaseAccessMutation = useUpdateFeatureBaseAccessPolicyMutation()
  const updateStatusMutation = useUpdateFeatureStatusMutation()
  const updateAccessMutation = useUpdateFeatureAccessPolicyMutation()
  const clearAccessMutation = useClearFeatureAccessPolicyMutation()
  // Personal-source changes reach FILTH's mass-purge guard; a held change is confirmed here.
  const personalPolicy = usePersonalPolicyConfirmation()
  const accessQuery = useAdminFeatureAccessPolicyQuery(accessFeatureId, isAccessDialogOpen)
  const featureAccessPolicy = accessQuery.data

  const selectedFeature = features?.find((f) => f.id === accessFeatureId)
  const inheritByDefault = selectedFeature?.access_overridden !== true

  const baseAccessConfigured = useMemo(() => {
    return isPolicyConfigured(baseAccessPolicy)
  }, [baseAccessPolicy])

  const handleToggleStatus = async (featureId: string, enabled: boolean) => {
    setUpdatingFeatureId(featureId)
    try {
      const result = await updateStatusMutation.mutateAsync({ featureId, enabled })
      personalPolicy.reportPush(result.policy_push)
    } catch {
      // Error handled by React Query mutation
    } finally {
      setUpdatingFeatureId(undefined)
    }
  }

  const allEnabledState = useMemo(() => {
    if (!features || features.length === 0) return false
    const enabledCount = features.filter((f) => f.enabled).length
    if (enabledCount === 0) return false
    if (enabledCount === features.length) return true
    return undefined
  }, [features])

  // Handler to open config dialog
  const handleEditFeatureConfig = async (featureId: string) => {
    if (
      featureId !== "dynamic_titles" &&
      featureId !== "responses_compaction" &&
      featureId !== "stream_backend_gap_mitigation" &&
      featureId !== "explorer_fast_router" &&
      featureId !== "explorer_steer"
    ) {
      return
    }

    setEditingFeatureId(featureId)
    setDynamicTitlesConfig(null)
    setResponsesCompactionConfig(null)
    setStreamGapMitigationConfig(null)
    setExplorerFastRouterConfig(null)
    setExplorerSteerConfig(null)
    try {
      const response = await fetchFeatureConfig<Record<string, unknown>>(featureId)
      if (featureId === "dynamic_titles") {
        setDynamicTitlesConfig(response.config as unknown as DynamicTitlesConfig)
      } else if (featureId === "responses_compaction") {
        setResponsesCompactionConfig(response.config as unknown as ResponsesCompactionConfig)
      } else if (featureId === "stream_backend_gap_mitigation") {
        setStreamGapMitigationConfig(response.config as unknown as StreamGapMitigationConfig)
      } else if (featureId === "explorer_steer") {
        setExplorerSteerConfig(response.config as unknown as ExplorerSteerConfig)
      } else {
        const raw = response.config as unknown as ExplorerFastRouterConfig
        setExplorerFastRouterConfig({
          ...raw,
          reasoning_presets: normalizeReasoningPresets(raw.reasoning_presets),
        })
      }
      setIsConfigDialogOpen(true)
    } catch (error) {
      toast.error(
        t("adminDashboard.features.configLoadError", "Failed to load configuration") +
          (error instanceof Error ? `: ${error.message}` : "")
      )
    }
  }

  // Handler to save config
  const handleSaveDynamicTitlesConfig = async (
    config: DynamicTitlesConfig,
    options?: { reason?: string; source?: "form" | "raw" }
  ) => {
    await updateFeatureConfig(
      "dynamic_titles",
      {
        topic_shift_enabled: config.topic_shift_enabled,
        topic_shift_min_messages: config.topic_shift_min_messages,
        topic_shift_check_interval: config.topic_shift_check_interval,
        auto_accept_first_improvement: config.auto_accept_first_improvement,
      },
      options
    )
    void queryClient.invalidateQueries({ queryKey: ["admin", "features"] })
  }

  const handleSaveResponsesCompactionConfig = async (config: ResponsesCompactionConfig) => {
    await updateFeatureConfig("responses_compaction", {
      trigger_tokens: config.trigger_tokens,
      min_messages: config.min_messages,
    })
    void queryClient.invalidateQueries({ queryKey: ["admin", "features"] })
  }

  const handleSaveStreamGapMitigationConfig = async (config: StreamGapMitigationConfig) => {
    await updateFeatureConfig("stream_backend_gap_mitigation", {
      tool_execution_timeout_seconds: config.tool_execution_timeout_seconds,
      max_tool_iterations: config.max_tool_iterations,
      web_content_indexing_timeout_seconds: config.web_content_indexing_timeout_seconds,
      web_content_retrieval_timeout_seconds: config.web_content_retrieval_timeout_seconds,
    })
    void queryClient.invalidateQueries({ queryKey: ["admin", "features"] })
  }

  const handleSaveExplorerFastRouterConfig = async (config: ExplorerFastRouterConfig) => {
    await updateFeatureConfig("explorer_fast_router", {
      deterministic_pregate_enabled: config.deterministic_pregate_enabled,
      model_router_enabled: config.model_router_enabled,
      confidence_threshold: config.confidence_threshold,
      timeout_ms: config.timeout_ms,
      fast_model_id: config.fast_model_id,
      reasoning_effort: config.reasoning_effort,
      model_routing_enabled: config.model_routing_enabled,
      serving_model_allowlist: config.serving_model_allowlist,
      reasoning_presets: config.reasoning_presets,
    })
    void queryClient.invalidateQueries({ queryKey: ["admin", "features"] })
    void queryClient.invalidateQueries({ queryKey: queryKeys.config })
  }

  const handleSaveExplorerSteerConfig = async (config: ExplorerSteerConfig) => {
    await updateFeatureConfig("explorer_steer", {
      inject_enabled: config.inject_enabled,
      stop_redirect_enabled: config.stop_redirect_enabled,
    })
    void queryClient.invalidateQueries({ queryKey: ["admin", "features"] })
    void queryClient.invalidateQueries({ queryKey: queryKeys.config })
  }

  const handleToggleAll = async (enabled: boolean) => {
    if (!features || features.length === 0) return

    setIsUpdatingAll(true)
    await queryClient.cancelQueries({ queryKey: ["admin", "features"] })

    const currentFeatures =
      queryClient.getQueryData<typeof features>(["admin", "features"]) || features
    const previousFeatures = currentFeatures
    const featuresToUpdate = currentFeatures.filter((f) => f.enabled !== enabled)

    queryClient.setQueryData(
      ["admin", "features"],
      currentFeatures.map((f) => ({ ...f, enabled }))
    )

    try {
      const updates = Object.fromEntries(featuresToUpdate.map((f) => [f.id, enabled]))
      const result = await updateFeaturesStatusBatch(updates)
      personalPolicy.reportPush(result.policy_push)
      void queryClient.invalidateQueries({ queryKey: ["admin", "features"] })
    } catch {
      queryClient.setQueryData(["admin", "features"], previousFeatures)
      await queryClient.invalidateQueries({ queryKey: ["admin", "features"] })
    } finally {
      setIsUpdatingAll(false)
    }
  }

  return (
    <>
      <CollapsibleManagementSection
        icon={<PlugZap className="size-5" />}
        id="features"
        storageKey="admin-management-features-expanded"
        title={t("adminDashboard.features.title", "Feature Access")}
        headerActions={
          <div className="flex items-center gap-4">
            <Button
              className="flex items-center gap-2 px-3"
              disabled={isLoading || isUpdatingAll}
              size="sm"
              type="button"
              variant="outline"
              onClick={() => setIsBaseAccessDialogOpen(true)}
            >
              {baseAccessConfigured ? (
                <ShieldCheck className="text-success size-4" />
              ) : (
                <Shield className="size-4" />
              )}
              {t("adminDashboard.features.baseAccess", { defaultValue: "Access for all" })}
            </Button>
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground text-sm">
                {t("adminDashboard.features.enableAll", { defaultValue: "Enable All" })}
              </span>
              <Switch
                aria-label={t("adminDashboard.features.enableAll", { defaultValue: "Enable All" })}
                checked={allEnabledState === true}
                disabled={isLoading || isUpdatingAll || !features || features.length === 0}
                onCheckedChange={handleToggleAll}
              />
            </div>
            <DelegationModeControl capability="features" />
          </div>
        }
      >
        {isLoading ? (
          <div className="space-y-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="flex items-center justify-between rounded-lg border p-4">
                <Skeleton className="h-4 w-40" />
                <Skeleton className="h-6 w-12" />
              </div>
            ))}
          </div>
        ) : (
          <FeaturesTable
            disableAll={isUpdatingAll}
            isUpdating={updatingFeatureId}
            features={(features || []).map((f) => ({
              id: f.id,
              name: f.name,
              description: f.description,
              enabled: f.enabled,
              access_configured: f.access_configured,
              has_config:
                f.id === "dynamic_titles" ||
                f.id === "responses_compaction" ||
                f.id === "stream_backend_gap_mitigation" ||
                f.id === "explorer_fast_router" ||
                f.id === "explorer_steer",
            }))}
            onEdit={(featureId) => setEditingFeatureRawId(featureId)}
            onEditConfig={handleEditFeatureConfig}
            onToggleStatus={handleToggleStatus}
            onEditAccess={(featureId) => {
              setAccessFeatureId(featureId)
              setIsAccessDialogOpen(true)
            }}
          />
        )}
      </CollapsibleManagementSection>

      <EditFeatureDialog
        featureId={editingFeatureRawId}
        isOpen={editingFeatureRawId !== null}
        onPolicyPush={personalPolicy.reportPush}
        onOpenChange={(open) => {
          if (!open) setEditingFeatureRawId(null)
        }}
      />

      <PersonalPolicyConfirmDialog
        state={personalPolicy.state}
        onConfirm={() => void personalPolicy.confirm()}
        onDismiss={personalPolicy.dismiss}
      />

      <AccessPolicyDialog
        isLoading={baseAccessQuery.isLoading}
        isSaving={updateBaseAccessMutation.isPending}
        loadError={baseAccessQuery.isError}
        open={isBaseAccessDialogOpen}
        policy={baseAccessPolicy}
        description={t("adminDashboard.features.baseAccessDescription", {
          defaultValue:
            "Default access policy applied to all features unless overridden per feature.",
        })}
        title={t("adminDashboard.features.baseAccessTitle", {
          defaultValue: "Base access policy — Features",
        })}
        onOpenChange={setIsBaseAccessDialogOpen}
        onSave={async (policy) => {
          try {
            await updateBaseAccessMutation.mutateAsync({ policy })
            toast.success(
              t("adminDashboard.access.saveSuccess", { defaultValue: "Access policy updated" })
            )
          } catch (error) {
            toast.error(
              t("adminDashboard.access.saveError", {
                defaultValue: "Failed to update access policy",
              })
            )
            throw error
          }
        }}
      />

      <AccessPolicyDialog
        isLoading={accessQuery.isLoading}
        isSaving={updateAccessMutation.isPending || clearAccessMutation.isPending}
        loadError={accessQuery.isError || baseAccessQuery.isError}
        open={isAccessDialogOpen}
        policy={featureAccessPolicy}
        description={t("adminDashboard.access.dialogDescription", {
          defaultValue: "Configure who can access this resource.",
        })}
        inherit={{
          enabled: true,
          inheritByDefault,
          policy: baseAccessPolicy,
          label: t("adminDashboard.features.inheritBaseAccess", {
            defaultValue: "Inherit access for all",
          }),
          description: t("adminDashboard.features.inheritBaseAccessDescription", {
            defaultValue:
              "When enabled, this feature uses the section baseline access policy. Disable to override for this feature.",
          }),
          onSaveInherited: async () => {
            if (!accessFeatureId) return
            const cleared = await clearAccessMutation.mutateAsync({ featureId: accessFeatureId })
            personalPolicy.reportPush(cleared.policy_push)
            void queryClient.invalidateQueries({ queryKey: ["admin", "features"] })
          },
        }}
        title={t("adminDashboard.access.dialogTitle", {
          defaultValue: "Access policy — {{name}}",
          name: selectedFeature?.name ?? accessFeatureId ?? "",
        })}
        onOpenChange={(open) => {
          setIsAccessDialogOpen(open)
          if (!open) {
            setAccessFeatureId(undefined)
          }
        }}
        onSave={async (policy) => {
          if (!accessFeatureId) return
          try {
            const saved = await updateAccessMutation.mutateAsync({
              featureId: accessFeatureId,
              policy,
            })
            personalPolicy.reportPush(saved.policy_push)
            toast.success(
              t("adminDashboard.access.saveSuccess", { defaultValue: "Access policy updated" })
            )
          } catch (error) {
            toast.error(
              t("adminDashboard.access.saveError", {
                defaultValue: "Failed to update access policy",
              })
            )
            throw error
          }
        }}
      />

      {/* Config dialog for dynamic_titles */}
      {editingFeatureId === "dynamic_titles" && dynamicTitlesConfig && (
        <EditDynamicTitlesDialog
          config={dynamicTitlesConfig}
          isOpen={isConfigDialogOpen}
          onSave={handleSaveDynamicTitlesConfig}
          onOpenChange={(open) => {
            setIsConfigDialogOpen(open)
            if (!open) {
              setEditingFeatureId(null)
              setDynamicTitlesConfig(null)
            }
          }}
        />
      )}

      {/* Config dialog for responses_compaction */}
      {editingFeatureId === "responses_compaction" && responsesCompactionConfig && (
        <EditResponsesCompactionDialog
          config={responsesCompactionConfig}
          isOpen={isConfigDialogOpen}
          onSave={handleSaveResponsesCompactionConfig}
          onOpenChange={(open) => {
            setIsConfigDialogOpen(open)
            if (!open) {
              setEditingFeatureId(null)
              setResponsesCompactionConfig(null)
            }
          }}
        />
      )}

      {/* Config dialog for stream_backend_gap_mitigation */}
      {editingFeatureId === "stream_backend_gap_mitigation" && streamGapMitigationConfig && (
        <EditStreamGapMitigationDialog
          config={streamGapMitigationConfig}
          isOpen={isConfigDialogOpen}
          onSave={handleSaveStreamGapMitigationConfig}
          onOpenChange={(open) => {
            setIsConfigDialogOpen(open)
            if (!open) {
              setEditingFeatureId(null)
              setStreamGapMitigationConfig(null)
            }
          }}
        />
      )}

      {/* Config dialog for explorer_fast_router */}
      {editingFeatureId === "explorer_fast_router" && explorerFastRouterConfig && (
        <EditExplorerFastRouterDialog
          config={explorerFastRouterConfig}
          isOpen={isConfigDialogOpen}
          onSave={handleSaveExplorerFastRouterConfig}
          onOpenChange={(open) => {
            setIsConfigDialogOpen(open)
            if (!open) {
              setEditingFeatureId(null)
              setExplorerFastRouterConfig(null)
            }
          }}
        />
      )}

      {/* Config dialog for explorer_steer */}
      {editingFeatureId === "explorer_steer" && explorerSteerConfig && (
        <EditExplorerSteerDialog
          config={explorerSteerConfig}
          isOpen={isConfigDialogOpen}
          onSave={handleSaveExplorerSteerConfig}
          onOpenChange={(open) => {
            setIsConfigDialogOpen(open)
            if (!open) {
              setEditingFeatureId(null)
              setExplorerSteerConfig(null)
            }
          }}
        />
      )}
    </>
  )
}
